import React, { useState, useEffect } from 'react';
import { useAuth } from '@/AuthContext';
import { db } from '@/lib/firebase';
import { collection, query, where, orderBy, limit, addDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { ToastContext } from '@/components/ToastProvider.jsx';

export default function ProductReviews({ productId, productName }) {
  const { user } = useAuth();
  const { notify } = React.useContext(ToastContext);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [checkingPermission, setCheckingPermission] = useState(true);
  const [newReview, setNewReview] = useState({
    rating: 5,
    comment: '',
    title: ''
  });

  // Check if user can review this product (has completed orders)
  useEffect(() => {
    const checkReviewPermission = async () => {
      if (!user?.uid || !productId) {
        setCanReview(false);
        setCheckingPermission(false);
        return;
      }

      try {
        setCheckingPermission(true);
        
        // Check if user has any completed orders for this product
        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef,
          where('userId', '==', user.uid),
          where('status', '==', 'completed'),
          limit(1)
        );
        
        const querySnapshot = await getDocs(q);
        
        // Check if any of the completed orders contain this product
        let hasProductInCompletedOrder = false;
        
        for (const doc of querySnapshot.docs) {
          const orderData = doc.data();
          if (orderData.items && Array.isArray(orderData.items)) {
            const hasProduct = orderData.items.some(item => 
              item.id === productId || item.productId === productId
            );
            if (hasProduct) {
              hasProductInCompletedOrder = true;
              break;
            }
          }
        }
        
        setCanReview(hasProductInCompletedOrder);
      } catch (error) {

        setCanReview(false);
      } finally {
        setCheckingPermission(false);
      }
    };

    checkReviewPermission();
  }, [user?.uid, productId]);

  // Fetch reviews for this product
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const reviewsRef = collection(db, 'productReviews');
        const q = query(
          reviewsRef,
          where('productId', '==', productId),
          where('approved', '==', true),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        
        const querySnapshot = await getDocs(q);
        const reviewsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setReviews(reviewsData);
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchReviews();
    }
  }, [productId]);

  // Calculate average rating
  const averageRating = reviews.length > 0 
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
    : 0;

  // Handle review submission
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!user) {
      notify('Please log in to leave a review', { type: 'error' });
      return;
    }

    if (!canReview) {
      notify('You can only review products you have purchased and received', { type: 'error' });
      return;
    }

    if (!newReview.comment.trim()) {
      notify('Please enter a review comment', { type: 'error' });
      return;
    }

    setSubmitting(true);
    
    try {
      const reviewData = {
        productId,
        productName,
        userId: user.uid,
        userName: user.displayName || 'Anonymous',
        userEmail: user.email,
        rating: newReview.rating,
        title: newReview.title.trim() || 'Great product!',
        comment: newReview.comment.trim(),
        approved: false, // Reviews need approval
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'productReviews'), reviewData);
      
      notify('Review submitted successfully! It will be visible after approval.', { type: 'success' });
      setNewReview({ rating: 5, comment: '', title: '' });
      setShowReviewForm(false);
      
      // Refresh reviews
      const reviewsRef = collection(db, 'productReviews');
      const q = query(
        reviewsRef,
        where('productId', '==', productId),
        where('approved', '==', true),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      
      const querySnapshot = await getDocs(q);
      const reviewsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setReviews(reviewsData);
    } catch (error) {
      
      notify('Failed to submit review. Please try again.', { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Star rating component
  const StarRating = ({ rating, interactive = false, onChange }) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? "button" : "button"}
          disabled={!interactive}
          onClick={interactive ? () => onChange(star) : undefined}
          className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={star <= rating ? "currentColor" : "none"}
            className={`${star <= rating ? "text-yellow-400" : "text-gray-300"}`}
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        </button>
      ))}
    </div>
  );

  if (loading || checkingPermission) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
        <span className="ml-2 text-sm text-gray-600">Loading reviews...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Reviews Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Customer Reviews</h3>
          <div className="flex items-center gap-3 mt-1">
            <StarRating rating={Math.round(averageRating)} />
            <span className="text-sm text-gray-600">
              {averageRating.toFixed(1)} out of 5
            </span>
            <span className="text-sm text-gray-500">
              ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
            </span>
          </div>
        </div>
        
        {/* Only show review button if user can review */}
        {user && canReview && (
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => setShowReviewForm(!showReviewForm)}
          >
            {showReviewForm ? 'Cancel' : 'Write a Review'}
          </button>
        )}
        
        {/* Show message if user can't review */}
        {user && !canReview && (
          <div className="text-sm text-gray-500">
            Purchase and complete an order to review this product
          </div>
        )}
        
        {/* Show login message if not logged in */}
        {!user && (
          <div className="text-sm text-gray-500">
            <a href="/login" className="text-blue-600 hover:underline">Login</a> to review this product
          </div>
        )}
      </div>

      {/* Review Form - Only show if user can review */}
      {showReviewForm && canReview && (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <h4 className="font-medium text-gray-900 mb-3">Write Your Review</h4>
          <form onSubmit={handleSubmitReview} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rating
              </label>
              <StarRating 
                rating={newReview.rating} 
                interactive={true} 
                onChange={(rating) => setNewReview(prev => ({ ...prev, rating }))}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Review Title (Optional)
              </label>
              <input
                type="text"
                className="input w-full"
                placeholder="e.g., Great quality, Fast delivery"
                value={newReview.title}
                onChange={(e) => setNewReview(prev => ({ ...prev, title: e.target.value }))}
                maxLength={100}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Review Comment *
              </label>
              <textarea
                className="input w-full"
                rows={4}
                placeholder="Share your experience with this product..."
                value={newReview.comment}
                onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                required
                maxLength={500}
              />
            </div>
            
            <div className="flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setShowReviewForm(false)}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-2">⭐</div>
          <p>No reviews yet. Be the first to review this product!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="border rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-medium text-gray-900">{review.title}</div>
                  <div className="text-sm text-gray-500">by {review.userName}</div>
                </div>
                {/* Customer review stars removed - only show title and author */}
              </div>
              
              <p className="text-gray-700 text-sm">{review.comment}</p>
              
              <div className="text-xs text-gray-400 mt-2">
                {review.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
